/**
 * Trophy Room ViewModel (Owner)
 * Story 3.6.d: Owner Trophy Room Management Frontend
 *
 * Handles all business logic for the authenticated owner's Trophy Room page.
 * Strict MVVM pattern - all logic here, page component is pure view.
 *
 * Structure: { state, actions, computed, onMount }
 *
 * Key Features:
 * - Fetch all items (public + private)
 * - Toggle visibility (optimistic update)
 * - Unpair items (optimistic update)
 * - Report lost/stolen status (optimistic update)
 * - Filter by product type (all, patches, clothing)
 * - Error rollback via re-fetch
 */

import { useMemo, useState, useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  getUserItemsAsync,
  updateItemVisibilityAsync,
  unpairItemAsync,
  clearTrophyRoom,
  clearError,
  selectTrophyRoom,
  selectTrophyRoomItems,
  selectTrophyRoomError,
  selectUpdatingVisibility,
  selectUnpairing,
} from '@/store/slices/trophyRoomSlice';
import type { TrophyRoomItem, ReportType } from '@/domain/ports/ITrophyRoomGateway';

export type FilterMode = 'all' | 'patches' | 'clothing';

export interface UseTrophyRoomViewModelReturn {
  state: {
    items: TrophyRoomItem[];
    loading: boolean;
    error: string | null;
    filterMode: FilterMode;
    updatingVisibility: string | null; // itemId being updated
    unpairing: string | null; // itemId being unpaired
  };
  actions: {
    fetchItems: () => Promise<void>;
    setFilterMode: (mode: FilterMode) => void;
    refresh: () => Promise<void>;
    updateItemVisibility: (itemId: string, visibility: 'public' | 'private') => Promise<void>;
    unpairItem: (itemId: string, reportType?: ReportType) => Promise<void>;
    dismissError: () => void;
  };
  computed: {
    filteredItems: TrophyRoomItem[];
    hasItems: boolean;
    publicItemCount: number;
    privateItemCount: number;
    totalItemCount: number;
    filteredItemCount: number;
    isEmptyState: boolean;
    hasActiveItems: boolean;
    hasLostItems: boolean;
    hasStolenItems: boolean;
  };
  onMount: () => void;
}

export const useTrophyRoomViewModel = (): UseTrophyRoomViewModelReturn => {
  const dispatch = useAppDispatch();
  const trophyRoom = useAppSelector(selectTrophyRoom);
  const items = useAppSelector(selectTrophyRoomItems);
  const error = useAppSelector(selectTrophyRoomError);
  const updatingVisibility = useAppSelector(selectUpdatingVisibility);
  const unpairing = useAppSelector(selectUnpairing);

  const [filterMode, setFilterMode] = useState<FilterMode>('all');

  // Actions
  const actions = {
    fetchItems: useCallback(async () => {
      await dispatch(getUserItemsAsync());
    }, [dispatch]),

    setFilterMode: useCallback((mode: FilterMode) => {
      setFilterMode(mode);
    }, []),

    refresh: useCallback(async () => {
      // Clear current data and refetch
      dispatch(clearTrophyRoom());
      await dispatch(getUserItemsAsync());
    }, [dispatch]),

    updateItemVisibility: useCallback(
      async (itemId: string, visibility: 'public' | 'private') => {
        const result = await dispatch(updateItemVisibilityAsync({ itemId, visibility }));

        // Rollback on error: re-fetch items
        if (updateItemVisibilityAsync.rejected.match(result)) {
          await dispatch(getUserItemsAsync());
        }
      },
      [dispatch]
    ),

    unpairItem: useCallback(
      async (itemId: string, reportType?: ReportType) => {
        const result = await dispatch(unpairItemAsync({ itemId, reportType }));

        // Rollback on error: re-fetch items
        if (unpairItemAsync.rejected.match(result)) {
          await dispatch(getUserItemsAsync());
        }
      },
      [dispatch]
    ),

    dismissError: useCallback(() => {
      dispatch(clearError());
    }, [dispatch]),
  };

  // State
  const state = {
    items,
    loading: trophyRoom.loading,
    error,
    filterMode,
    updatingVisibility,
    unpairing,
  };

  // Computed values
  const computed = {
    filteredItems: useMemo(() => {
      if (filterMode === 'all') {
        return items;
      }

      if (filterMode === 'patches') {
        // Only items with productType === 'patch' or 'patches'
        return items.filter(
          (item) =>
            item.product?.productType === 'patch' ||
            item.product?.productType === 'patches'
        );
      }

      if (filterMode === 'clothing') {
        // All non-patch types are clothing (hoodie, jacket, shirt, pants, etc.)
        return items.filter(
          (item) =>
            item.product?.productType !== 'patch' &&
            item.product?.productType !== 'patches'
        );
      }

      return items;
    }, [items, filterMode]),

    hasItems: items.length > 0,

    publicItemCount: items.filter((item) => item.visibility === 'public').length,

    privateItemCount: items.filter((item) => item.visibility === 'private').length,

    totalItemCount: items.length,

    filteredItemCount: 0, // Will be computed after filteredItems

    // Empty state: data loaded, no error, not loading, and no items
    isEmptyState: !state.loading && !state.error && items.length === 0,

    hasActiveItems: items.some((item) => item.status === 'active'),

    hasLostItems: items.some((item) => item.status === 'lost'),

    hasStolenItems: items.some((item) => item.status === 'stolen'),
  };

  // Recompute filtered item count (can't be in useMemo above due to circular dependency)
  computed.filteredItemCount = computed.filteredItems.length;

  // onMount - called once when component mounts
  const onMount = useCallback(() => {
    dispatch(getUserItemsAsync());

    // Cleanup: clear Trophy Room data when unmounting
    return () => {
      dispatch(clearTrophyRoom());
    };
  }, [dispatch]);

  // Effect: Dismiss error after 5 seconds
  useEffect(() => {
    if (error) {
      const timeoutId = setTimeout(() => {
        dispatch(clearError());
      }, 5000);

      return () => clearTimeout(timeoutId);
    }
  }, [error, dispatch]);

  return { state, actions, computed, onMount };
};
